import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { invalidateAll } from '../api/apiCache';
import { invalidateInsightsCache } from '../api/insightsCache';
import api, { AGENCE_LOGOUT_EVENT, refreshAccess } from '../api/client';
import {
  isNativePlatform,
  ensureAndroidNotificationChannel,
  hasPromptedOnThisDevice,
  registerForPushNotifications,
  subscribeToPushTokenRefresh,
  unregisterPushNotifications,
} from '../lib/pushNotifications';
import { configurePurchases, logOutPurchases } from '../lib/purchases';
import { userIdFromToken } from '../lib/authToken';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  // true while we're attempting a silent refresh on startup — prevents PrivateRoute
  // from redirecting to /login before we've had a chance to recover the session.
  const [initializing, setInitializing] = useState(() => !localStorage.getItem('token'));

  // On mount, if there's no token in localStorage, attempt a silent refresh using
  // the httpOnly refresh cookie. This handles the case where the access token was
  // cleared (expired, deleted in DevTools, etc.) but the session is still valid.
  //
  // Gated on 'hadSession' (a non-sensitive marker set on login, cleared on any
  // definitive logout/refresh-failure) so this doesn't run for a visitor who's never
  // logged in — that's the common case for anyone landing on /login, and firing a
  // refresh call we know has no valid cookie behind it just produces a needless
  // visible 401 in the console/network tab for a fresh, unauthenticated visitor.
  useEffect(() => {
    if (localStorage.getItem('token')) return; // already have a token — nothing to do
    if (!localStorage.getItem('hadSession')) { setInitializing(false); return; } // never logged in (or already known logged out) — nothing to recover
    refreshAccess()
      .then(newToken => { setToken(newToken); })
      .catch(() => { localStorage.removeItem('hadSession'); }) // no valid session — stay logged out, don't retry next mount
      .finally(() => setInitializing(false));
  }, []);

  const login = useCallback((newToken) => {
    invalidateInsightsCache();
    localStorage.setItem('token', newToken);
    localStorage.setItem('hadSession', '1');
    localStorage.setItem('chat_session_start', new Date().toISOString());
    setToken(newToken);
  }, []);

  const logout = useCallback(async () => {
    // Clear local session state first and unconditionally — confirmed real bug on a native
    // device (2026-09-12): awaiting the server revoke + push-unregister calls sequentially
    // meant a hung native plugin call (or a slow/unresponsive network request) left the user
    // stuck signed in with no feedback, since setToken(null) never ran. Sign-out must never
    // be gated behind best-effort cleanup that isn't guaranteed to ever settle.
    localStorage.removeItem('token');
    localStorage.removeItem('hadSession');
    localStorage.removeItem('chat_session_start');
    invalidateAll();
    invalidateInsightsCache();
    setToken(null);
    // Tell the server to revoke the refresh token and unregister this device's push token —
    // best-effort, fire-and-forget, never awaited by the code path that determines sign-out.
    api.post('/auth/logout').catch(() => {});
    unregisterPushNotifications().catch(() => {});
    // RevenueCat identity must be cleared too, or the next account to sign in on this
    // device inherits the previous one's entitlements. Grocerly has this exact gap.
    // Fire-and-forget for the same reason as the two calls above.
    logOutPurchases().catch(() => {});
  }, []);

  // Push notifications (docs/TODO.md §1a). Android's channel is unconditional and idempotent
  // (app-store-playbook: guard to Android only, harmless to call every load). The permission
  // prompt fires at most once per device — confirmed post-onboarding, not blind on every
  // sign-in like a naive first cut would do, since an unprimed ask this early in a finance
  // app risks a reflexive, unrecoverable decline (iOS shows its system dialog only once
  // ever). The Settings-page toggle is the always-available fallback for anyone who declines
  // here. requestPermissions() is idempotent once answered, so re-running this on a later
  // session for a user who already answered is harmless — hasPromptedOnThisDevice() just
  // avoids the redundant plugin round trip.
  useEffect(() => {
    if (!token || !isNativePlatform()) return;
    ensureAndroidNotificationChannel().catch(() => {});
    if (!hasPromptedOnThisDevice()) {
      registerForPushNotifications().catch(() => {});
    }
  }, [token]);

  // Keeps the server's copy of this device's token current across FCM's own rotation events
  // for as long as a session is active.
  useEffect(() => {
    if (!token || !isNativePlatform()) return undefined;
    return subscribeToPushTokenRefresh();
  }, [token]);

  // Listen for forced logout events dispatched by the 401 interceptor in client.js
  // (triggered when the refresh token is also expired or invalid)
  useEffect(() => {
    function handleForcedLogout() {
      localStorage.removeItem('hadSession');
      invalidateAll();
      invalidateInsightsCache();
      setToken(null);
    }
    window.addEventListener(AGENCE_LOGOUT_EVENT, handleForcedLogout);
    return () => window.removeEventListener(AGENCE_LOGOUT_EVENT, handleForcedLogout);
  }, []);

  // Identify the user to RevenueCat whenever the session changes, including a session
  // recovered silently via the refresh cookie (which never passes through login()).
  // Best-effort by contract: configurePurchases() is a no-op off-native or without a
  // platform key, and swallows its own failures — billing must never gate app usage.
  useEffect(() => {
    const userId = userIdFromToken(token);
    if (userId) configurePurchases(userId);
  }, [token]);

  const value = useMemo(
    () => ({ token, login, logout, isAuthenticated: !!token, initializing }),
    [token, login, logout, initializing]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
